/**
 * EmailPreviewModal — Modal overlay that shows a rendered email preview in an iframe.
 * Allows users to see exactly how the daily email will look before sending.
 */

import { useEffect, useState, useRef } from "react";
import { getEmailPreview, EmailPreviewData } from "../utils/PowerBIApiCall";

interface EmailPreviewModalProps {
  portcoId: string;
  calloutMessage?: string;
  /** YYYY-MM-DD; empty string or undefined = latest available data. */
  reportDate?: string;
  onClose: () => void;
}

export function EmailPreviewModal({
  portcoId,
  calloutMessage,
  reportDate,
  onClose,
}: EmailPreviewModalProps) {
  const [preview, setPreview] = useState<EmailPreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    async function loadPreview() {
      try {
        setLoading(true);
        setError(null);
        const data = await getEmailPreview(
          portcoId,
          calloutMessage,
          reportDate || undefined,
        );
        setPreview(data);
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Failed to load email preview",
        );
      } finally {
        setLoading(false);
      }
    }
    loadPreview();
  }, [portcoId, calloutMessage, reportDate]);

  // Write HTML into iframe once loaded
  useEffect(() => {
    if (preview?.html && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(preview.html);
        doc.close();
      }
    }
  }, [preview]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-950/50 backdrop-blur-[2px]" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-pop border border-gray-200/70 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Email Preview
            </h2>
            {preview && (
              <p className="text-sm text-gray-500 mt-0.5">{preview.subject}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {reportDate && (
          <div className="px-6 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-800">
            Showing report as of {reportDate}. Open orders reflect the current
            state, not the selected date.
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-wmp-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  Generating email preview...
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
                  <svg
                    className="w-5 h-5 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <p className="text-sm text-gray-700 font-medium">
                  Failed to load preview
                </p>
                <p className="text-xs text-gray-500 mt-1">{error}</p>
              </div>
            </div>
          )}

          {preview && !loading && (
            <div
              className="bg-white rounded-lg shadow-sm overflow-hidden mx-auto"
              style={{ maxWidth: 960 }}
            >
              <iframe
                ref={iframeRef}
                title="Email Preview"
                className="w-full border-0"
                style={{ minHeight: 600, height: "100%" }}
                sandbox="allow-same-origin"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <p className="text-xs text-gray-400">
            This is how the daily email will appear in recipients' inboxes.
          </p>
          <button
            onClick={onClose}
            className="text-sm px-4 py-1.5 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
